import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  PhoneCall, Phone, MessageCircle, CheckCircle2, Clock, AlertTriangle,
  Users, RefreshCw, Search, ChevronRight, SlidersHorizontal, CheckSquare,
  Sparkles, Stethoscope, UserCheck, CalendarCheck, ShieldAlert
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDate, telLink, waLink, initials, inr, todayIST } from "@/lib/format";
import { PageHeader, Loading, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const STATUSES = [
  { value: "pending", label: "Pending Follow-up" },
  { value: "contacted", label: "Contacted / In Progress" },
  { value: "resolved", label: "Resolved" },
  { value: "closed", label: "Closed" },
];

export default function FollowUps() {
  const { user, settings } = useAuth();
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending");
  const [typeFilter, setTypeFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [templateLang, setTemplateLang] = useState("hinglish"); // hinglish | hindi | english

  // Complete Treatment Modal
  const [completeOpen, setCompleteOpen] = useState(false);
  const [selectedForComplete, setSelectedForComplete] = useState(null);
  const [completeNote, setCompleteNote] = useState("");
  const [completePainScore, setCompletePainScore] = useState("");
  const [completing, setCompleting] = useState(false);

  const clinicPhone = settings?.phone || "9431100000";

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (typeFilter !== "all") params.type = typeFilter;
      const res = await api.get("/follow-ups", { params });
      setItems(res.data.items || []);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Update Follow-up Status
  const handleUpdateStatus = async (id, newStatus) => {
    try {
      await api.patch(`/follow-ups/${id}`, { status: newStatus });
      toast.success("Follow-up status updated");
      setItems((prev) =>
        prev.map((f) => (f.id === id ? { ...f, status: newStatus } : f))
      );
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  // Complete Treatment Handler
  const handleConfirmComplete = async () => {
    if (!selectedForComplete) return;
    setCompleting(true);
    try {
      const pid = selectedForComplete.patient_id;
      await api.post(`/patients/${pid}/complete`, {
        completion_date: todayIST(),
        note: completeNote || "Treatment completed via patient follow-up confirmation.",
        final_pain_score: completePainScore !== "" ? parseInt(completePainScore) : null,
      });
      // Close follow-up
      await api.patch(`/follow-ups/${selectedForComplete.id}`, {
        status: "resolved",
        notes: `Patient confirmed treatment completed. ${completeNote}`,
      });
      toast.success(`${selectedForComplete.patient_name}: Treatment marked completed!`);
      setCompleteOpen(false);
      loadData();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setCompleting(false);
    }
  };

  // Multilingual WhatsApp Message Generator for 1st, 2nd, 3rd, 4th absence stages
  const getAbsenceWhatsAppMessage = (patientName, absentCount) => {
    const pName = patientName || "Sir/Ma'am";
    const count = Number(absentCount) || 1;

    if (count === 1) {
      if (templateLang === "hindi") {
        return `नमस्ते ${pName} जी, बीके स्पाइन केयर फिजियोथेरेपी क्लिनिक से।\n\nआज आपका फिजियोथेरेपी सेशन छूट गया है। निरंतर इलाज से ही दर्द में तेजी से पूरा आराम मिलता है। क्या आप कल के सेशन के लिए समय तय करना चाहेंगे? कृपया हमें बताएं। धन्यवाद!`;
      }
      if (templateLang === "english") {
        return `Dear ${pName}, greetings from BK Spine Care Physiotherapy Clinic.\n\nWe missed you at your therapy session today. Consistent treatment is essential for steady recovery. Please let us know if we can schedule your session for tomorrow. Thank you!`;
      }
      return `Namaste ${pName} ji, BK Spine Care Physiotherapy Clinic se.\n\nAaj aapka physiotherapy session miss ho gaya hai. Regular therapy lene se hi pain me fast relief milta hai. Kya aap kal ka time confirm kar sakte hain? Dhanyavaad!`;
    } else if (count === 2) {
      if (templateLang === "hindi") {
        return `नमस्ते ${pName} जी, बीके स्पाइन केयर क्लिनिक से।\n\nआप लगातार 2 दिन से क्लिनिक नहीं आ पाए हैं। फिजियोथेरेपी बीच में रुकने से दर्द दोबारा बढ़ सकता है। यदि समय या कोई अन्य समस्या है तो कृपया हमें बताएं ताकि हम समय समायोजित कर सकें।`;
      }
      if (templateLang === "english") {
        return `Dear ${pName}, we noticed you missed 2 consecutive physiotherapy sessions at BK Spine Care.\n\nPausing therapy prematurely may delay your recovery. Please reply to this message or call us if you need to adjust your appointment timing.`;
      }
      return `Hello ${pName} ji, aap lagatar 2 din se BK Spine Care nahi aa paye hain.\n\nTreatment beech me rukne se pain wapas aa sakta hai. Agar timing me koi problem hai to kripya hume batayein taaki hum schedule adjust kar sakein.`;
    } else if (count === 3) {
      if (templateLang === "hindi") {
        return `नमस्ते ${pName} जी, बीके स्पाइन केयर से जरूरी सूचना।\n\nआपके लगातार 3 सेशन मिस हो चुके हैं। डॉ. बी.के. शर्मा जी ने आपकी रीढ़/जोड़ों की रिकवरी के लिए नियमित थेरेपी की सलाह दी थी। कृपया आज ही क्लिनिक आएं या संपर्क करें: ${clinicPhone}.`;
      }
      if (templateLang === "english") {
        return `Dear ${pName}, urgent update from BK Spine Care.\n\nYou have missed 3 consecutive therapy sessions. Dr. Sharma strongly advises continuing your prescribed physiotherapy to avoid relapse. Please call us at ${clinicPhone} today.`;
      }
      return `Namaste ${pName} ji, aapke 3 sessions lagatar miss ho chuke hain.\n\nDr. B.K. Sharma ji ne aapki complete recovery ke liye regular therapy advise ki thi. Kripya aaj hi clinic visit karein ya call karein: ${clinicPhone}.`;
    } else {
      // 4th+ absence
      if (templateLang === "hindi") {
        return `अति आवश्यक सूचना: ${pName} जी, आप 4 या अधिक दिनों से अनुपस्थित हैं।\n\nआपका फिजियोथेरेपी कोर्स बाधित हो रहा है। यदि आप पूर्ण रूप से स्वस्थ हो चुके हैं या कोई परेशानी है, तो कृपया क्लिनिक को सूचित करें: ${clinicPhone}.`;
      }
      if (templateLang === "english") {
        return `IMPORTANT NOTICE: Dear ${pName}, 4+ consecutive missed therapy sessions detected.\n\nYour treatment plan is interrupted. If your treatment is complete or you require assistance, please inform BK Spine Care immediately at ${clinicPhone}.`;
      }
      return `URGENT: ${pName} ji, aap 4 din se clinic se absent hain.\n\nAapka physiotherapy treatment course interrupt ho raha hai. Agar aapka pain poora theek ho gaya hai ya koi problem hai, to kripya hume update karein: ${clinicPhone}.`;
    }
  };

  // Filtered Items
  const filtered = useMemo(() => {
    if (!search) return items;
    const q = search.toLowerCase();
    return items.filter(
      (f) =>
        f.patient_name?.toLowerCase().includes(q) ||
        f.patient_code?.toLowerCase().includes(q) ||
        f.patient_phone?.includes(q) ||
        f.notes?.toLowerCase().includes(q)
    );
  }, [items, search]);

  return (
    <div className="space-y-5">
      <PageHeader
        title="Patient Retention & Follow-ups"
        subtitle="Consecutive absence tracking, 4-stage escalation progress, and personalized WhatsApp recovery follow-ups"
        testid="followups-page"
        action={
          <Button
            size="sm"
            onClick={loadData}
            variant="outline"
            className="gap-1.5 bg-white"
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        }
      />

      {/* Filter and Language Toolbar */}
      <Card className="p-3.5 bg-slate-50/90 border-slate-200">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          {/* Status and Type Filters */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-semibold text-slate-500">Status:</span>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-8 text-xs w-44 bg-white font-medium">
                <SelectValue placeholder="Filter Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Follow-ups</SelectItem>
                <SelectItem value="pending">Pending ({items.filter(i => i.status === "pending").length})</SelectItem>
                <SelectItem value="contacted">Contacted ({items.filter(i => i.status === "contacted").length})</SelectItem>
                <SelectItem value="resolved">Resolved ({items.filter(i => i.status === "resolved").length})</SelectItem>
                <SelectItem value="closed">Closed ({items.filter(i => i.status === "closed").length})</SelectItem>
              </SelectContent>
            </Select>

            {/* Template Language Selector */}
            <div className="flex items-center gap-1.5 ml-2">
              <span className="text-xs font-semibold text-slate-500">WhatsApp Language:</span>
              <div className="flex bg-white rounded-md border p-0.5">
                <button
                  type="button"
                  onClick={() => setTemplateLang("hinglish")}
                  className={`px-2 py-0.5 text-xs font-medium rounded ${
                    templateLang === "hinglish" ? "bg-bk-blue text-white" : "text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  Hinglish
                </button>
                <button
                  type="button"
                  onClick={() => setTemplateLang("hindi")}
                  className={`px-2 py-0.5 text-xs font-medium rounded ${
                    templateLang === "hindi" ? "bg-bk-blue text-white" : "text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  Hindi
                </button>
                <button
                  type="button"
                  onClick={() => setTemplateLang("english")}
                  className={`px-2 py-0.5 text-xs font-medium rounded ${
                    templateLang === "english" ? "bg-bk-blue text-white" : "text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  English
                </button>
              </div>
            </div>
          </div>

          {/* Quick Search */}
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400" />
            <Input
              placeholder="Search patient, phone, notes..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-8 text-xs bg-white"
            />
          </div>
        </div>
      </Card>

      {/* Follow-up Cards Grid */}
      {loading ? (
        <Loading label="Loading follow-up queue..." />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={PhoneCall}
          title="No follow-ups in queue"
          subtitle="Absent patients and treatment reminders will appear here automatically."
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((fu) => {
            const count = Math.min(Math.max(Number(fu.absent_count) || 1, 1), 4);
            const rawCount = Number(fu.absent_count) || 1;

            // 4-stage color progress bar configuration
            const progressPct = count === 1 ? 25 : count === 2 ? 50 : count === 3 ? 75 : 100;
            const progressColor =
              count === 1
                ? "bg-emerald-500"
                : count === 2
                ? "bg-amber-500"
                : count === 3
                ? "bg-blue-600"
                : "bg-red-600";

            const progressLabel =
              count === 1
                ? "1st Absent (25%)"
                : count === 2
                ? "2nd Consecutive Absent (50%)"
                : count === 3
                ? "3rd Consecutive Absent (75%)"
                : `${rawCount}th Consecutive Absent (100% Critical)`;

            const waMsg = getAbsenceWhatsAppMessage(fu.patient_name, rawCount);
            const waUrl = waLink(fu.patient_whatsapp || fu.patient_phone, waMsg);

            return (
              <Card
                key={fu.id}
                className="p-4 hover:shadow-md transition-all border-slate-200 flex flex-col justify-between bg-white relative overflow-hidden"
              >
                {/* Top Accent bar */}
                <div className={`absolute top-0 left-0 right-0 h-1.5 ${progressColor}`} />

                <div className="space-y-3 pt-1">
                  {/* Header: Avatar, Name, Code */}
                  <div className="flex items-start justify-between gap-2.5">
                    <div className="flex items-start gap-2.5 min-w-0">
                      <Avatar
                        className="h-10 w-10 cursor-pointer ring-2 ring-slate-100 hover:ring-bk-blue transition-all shrink-0"
                        onClick={() => navigate(`/patients/${fu.patient_id}`)}
                      >
                        <AvatarFallback className="bg-blue-50 text-bk-blue font-bold text-xs">
                          {initials(fu.patient_name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <button
                          onClick={() => navigate(`/patients/${fu.patient_id}`)}
                          className="font-bold text-slate-900 text-sm hover:text-bk-blue text-left truncate block max-w-[170px] leading-tight"
                        >
                          {fu.patient_name}
                        </button>
                        <p className="text-[11px] text-slate-400 font-mono mt-0.5">
                          {fu.patient_code || "BK Patient"}
                        </p>
                      </div>
                    </div>

                    {/* Status Dropdown */}
                    <div className="shrink-0 w-32">
                      <Select
                        value={fu.status || "pending"}
                        onValueChange={(val) => handleUpdateStatus(fu.id, val)}
                      >
                        <SelectTrigger className="h-7 text-[11px] bg-slate-50 font-medium">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {STATUSES.map((st) => (
                            <SelectItem key={st.value} value={st.value} className="text-xs">
                              {st.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* 4-Stage Absence Escalation Progress Bar */}
                  <div className="space-y-1 bg-slate-50/90 p-2 rounded-lg border border-slate-100">
                    <div className="flex items-center justify-between text-[11px] font-semibold text-slate-700">
                      <span>Absence Escalation:</span>
                      <span className="font-bold">{progressLabel}</span>
                    </div>
                    {/* Visual 4-Segment Progress Bar */}
                    <div className="w-full h-2 bg-slate-200 rounded-full overflow-hidden flex">
                      <div
                        className={`h-full transition-all duration-500 rounded-full ${progressColor}`}
                        style={{ width: `${progressPct}%` }}
                      />
                    </div>
                  </div>

                  {/* Clinical & Visit Details */}
                  <div className="grid grid-cols-2 gap-2 text-xs p-2 bg-slate-50/60 rounded border border-slate-100">
                    <div>
                      <p className="text-[10px] uppercase font-semibold text-slate-400">Last Visit Date</p>
                      <p className="font-bold text-slate-800 mt-0.5">
                        {fu.last_visit_date ? fmtDate(fu.last_visit_date) : "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase font-semibold text-slate-400">Consecutive Absences</p>
                      <p className={`font-bold mt-0.5 ${rawCount >= 3 ? "text-red-600 font-extrabold" : "text-slate-800"}`}>
                        {rawCount} {rawCount === 1 ? "day" : "days"} absent
                      </p>
                    </div>
                  </div>

                  {fu.notes && (
                    <p className="text-xs text-slate-600 line-clamp-1 italic bg-amber-50/50 px-2 py-1 rounded border border-amber-100/60">
                      Note: {fu.notes}
                    </p>
                  )}
                </div>

                {/* Footer Action Bar */}
                <div className="mt-3 pt-2.5 border-t border-slate-100 flex items-center justify-between gap-1.5">
                  {/* Phone Call */}
                  <a href={telLink(fu.patient_phone)} title="Call Patient">
                    <Button size="sm" variant="outline" className="h-7 w-7 p-0 text-bk-blue">
                      <Phone className="h-3.5 w-3.5" />
                    </Button>
                  </a>

                  {/* WhatsApp with 4-Stage Pre-filled Message */}
                  <a
                    href={waUrl}
                    target="_blank"
                    rel="noreferrer"
                    title={`Send Stage ${count} WhatsApp message`}
                  >
                    <Button size="sm" className="h-7 px-2 text-[11px] bg-bk-green hover:bg-emerald-700 text-white gap-1">
                      <MessageCircle className="h-3.5 w-3.5" /> WhatsApp ({templateLang.toUpperCase()})
                    </Button>
                  </a>

                  {/* Complete Treatment Action Button */}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setSelectedForComplete(fu);
                      setCompleteNote("");
                      setCompletePainScore("");
                      setCompleteOpen(true);
                    }}
                    className="h-7 px-2 text-[11px] text-bk-blue border-blue-200 hover:bg-blue-50"
                    title="Patient confirmed treatment completed"
                  >
                    <CheckSquare className="h-3 w-3 mr-1" /> Complete Treatment
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Complete Treatment Modal */}
      <Dialog open={completeOpen} onOpenChange={setCompleteOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2 text-bk-blue">
              <CheckCircle2 className="h-5 w-5 text-bk-green" /> Mark Treatment Completed
            </DialogTitle>
            <DialogDescription className="text-xs">
              When patient confirms full recovery or finishes therapy course during follow-up call.
            </DialogDescription>
          </DialogHeader>
          {selectedForComplete && (
            <div className="space-y-3 py-2">
              <div className="p-3 bg-slate-50 border rounded-lg text-xs space-y-1">
                <p className="font-bold text-slate-800">
                  {selectedForComplete.patient_name} ({selectedForComplete.patient_code || "BK Patient"})
                </p>
                <p className="text-slate-600">
                  Marking treatment completed will close this follow-up and update patient's status to Completed.
                </p>
              </div>

              <div className="space-y-1.5">
                <Label>Final Pain Score (0 - 10, Optional)</Label>
                <Input
                  type="number"
                  min="0"
                  max="10"
                  placeholder="e.g. 0 (Pain-free) or 1"
                  value={completePainScore}
                  onChange={(e) => setCompletePainScore(e.target.value)}
                />
              </div>

              <div className="space-y-1.5">
                <Label>Follow-up Outcome / Note *</Label>
                <Textarea
                  rows={3}
                  placeholder="e.g. Patient called and reported 100% relief. Exercises advised at home."
                  value={completeNote}
                  onChange={(e) => setCompleteNote(e.target.value)}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompleteOpen(false)}>Cancel</Button>
            <Button
              onClick={handleConfirmComplete}
              disabled={completing}
              className="bg-bk-green hover:bg-emerald-700 text-white"
            >
              {completing ? "Completing..." : "Confirm Treatment Complete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
